import { useMemo, useState, type CSSProperties } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import {
  useStore,
  isCompNode,
  isNetLabelNode,
  isPowerSymbolNode,
  isSpliceNode,
  type AnyNode,
} from '../store';
import { showToast } from './Toast';

interface ConnRow {
  edgeId: string;
  sheetId: string;
  sheetName: string;
  wireNo: string;
  from: string;
  to: string;
  net: string;
  /** label fallback shown as placeholder when no explicit netName (old projects) */
  netFallback: string;
  gauge: string;
  color: string;
  color2: string;
  harnessId: string;
}

function endpointLabel(nodes: Map<string, AnyNode>, nodeId: string | null | undefined, handle: string | null | undefined): string {
  if (!nodeId) return '?';
  const n = nodes.get(nodeId);
  if (!n) return '?';
  if (isCompNode(n)) {
    const pin = n.data.pins.find((p) => p.id === handle);
    return `${n.data.label}.${pin?.name ?? handle ?? '?'}`;
  }
  if (isPowerSymbolNode(n) || isNetLabelNode(n)) return `⏚ ${n.data.name}`;
  if (isSpliceNode(n)) return `SPLICE ${n.data.label}`;
  return nodeId;
}

/**
 * EPLAN / AutoCAD-Electrical style connection list: every conductor is a row.
 * The wire is the unit of manufacturing — edit its number, gauge, colour, net
 * and (the point of this view) bulk-assign rows to a physical harness.
 */
export function ConnectionsModal({ onClose }: { onClose: () => void }) {
  const sheets = useStore((s) => s.sheets);
  const assignHarness = useStore((s) => s.assignHarnessToEdgesAcrossSheets);
  const updateEdges = useStore((s) => s.updateEdgesAcrossSheets);
  const createHarnessForEdges = useStore((s) => s.createHarnessForEdges);
  const goToEdge = useStore((s) => s.goToEdge);

  const bundles = useMemo(
    () => sheets.flatMap((sh) => (sh.kind === 'interconnect' ? sh.bundles ?? [] : [])),
    [sheets],
  );
  const harnessName = useMemo(() => new Map(bundles.map((b) => [b.id, b.name])), [bundles]);

  const rows = useMemo<ConnRow[]>(() => {
    const out: ConnRow[] = [];
    for (const sh of sheets) {
      if (sh.kind !== 'schematic') continue;
      const nodes = new Map(sh.nodes.map((n) => [n.id, n]));
      for (const e of sh.edges) {
        out.push({
          edgeId: e.id,
          sheetId: sh.id,
          sheetName: sh.name,
          wireNo: String(e.data?.wireNo ?? ''),
          from: endpointLabel(nodes, e.source, e.sourceHandle),
          to: endpointLabel(nodes, e.target, e.targetHandle),
          net: String(e.data?.netName ?? ''),
          netFallback: String(e.data?.label ?? (typeof e.label === 'string' ? e.label : '') ?? ''),
          gauge: String(e.data?.gauge ?? ''),
          color: String(e.data?.color ?? ''),
          color2: String(e.data?.color2 ?? ''),
          harnessId: String(e.data?.harnessId ?? ''),
        });
      }
    }
    return out.sort((a, b) =>
      a.wireNo && b.wireNo
        ? a.wireNo.localeCompare(b.wireNo, undefined, { numeric: true })
        : a.from.localeCompare(b.from),
    );
  }, [sheets]);

  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkHarness, setBulkHarness] = useState('');

  const query = q.trim().toLowerCase();
  const visible = query
    ? rows.filter((r) =>
        `${r.wireNo} ${r.from} ${r.to} ${r.net} ${r.gauge} ${harnessName.get(r.harnessId) ?? ''}`
          .toLowerCase()
          .includes(query),
      )
    : rows;

  const allChecked = visible.length > 0 && visible.every((r) => selected.has(r.edgeId));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) visible.forEach((r) => next.delete(r.edgeId));
      else visible.forEach((r) => next.add(r.edgeId));
      return next;
    });
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const applyBulkHarness = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    assignHarness(ids, bulkHarness || undefined);
  };
  const createHarness = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const id = createHarnessForEdges(ids);
    if (id) showToast('Created auto-named harness for selected wires', 'success');
  };

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <span style={titleStyle}>Connection list</span>
          <button style={closeBtnStyle} onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        <div style={toolbarStyle}>
          <div style={{ position: 'relative', width: 220 }}>
            <SearchIcon size={13} style={searchIconStyle} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search connections…"
              style={searchInputStyle}
            />
          </div>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selected.size} selected</span>
          <button
            style={{ ...toolBtnStyle, opacity: selected.size ? 1 : 0.45 }}
            disabled={selected.size === 0}
            onClick={createHarness}
            title="Create a new auto-named harness and assign the selected wires"
          >
            New harness
          </button>
          <select value={bulkHarness} onChange={(e) => setBulkHarness(e.target.value)} style={selectStyle}>
            <option value="">No harness</option>
            {bundles.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || '(unnamed)'}
              </option>
            ))}
          </select>
          <button
            style={{ ...primaryBtnStyle, opacity: selected.size ? 1 : 0.45 }}
            disabled={selected.size === 0}
            onClick={applyBulkHarness}
            title="Assign the selected wires to the chosen harness"
          >
            Assign to selected
          </button>
        </div>

        <div style={listStyle}>
          <table style={tableStyle}>
            <colgroup>
              <col style={{ width: 30 }} />
              <col style={{ width: 58 }} />
              <col style={{ width: 205 }} />
              <col style={{ width: 205 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 76 }} />
              <col style={{ width: 102 }} />
              <col style={{ width: 164 }} />
              <col style={{ width: 34 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thStyle}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                </th>
                <th style={thStyle}>#</th>
                <th style={thStyle}>From</th>
                <th style={thStyle}>To</th>
                <th style={thStyle}>Net</th>
                <th style={thStyle}>Gauge</th>
                <th style={thStyle}>Color</th>
                <th style={thStyle}>Harness</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={9}>
                    No connections yet — draw wires between component pins on a schematic sheet.
                  </td>
                </tr>
              )}
              {visible.map((r) => (
                <tr key={r.edgeId} style={selected.has(r.edgeId) ? selectedRowStyle : undefined}>
                  <td style={tdStyle}>
                    <input type="checkbox" checked={selected.has(r.edgeId)} onChange={() => toggle(r.edgeId)} />
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={r.wireNo}
                      placeholder="—"
                      onChange={(e) => updateEdges([r.edgeId], { wireNo: e.target.value.trim() || undefined })}
                      style={cellInputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={truncStyle} title={r.from}>{r.from}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={truncStyle} title={r.to}>{r.to}</div>
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={r.net}
                      placeholder={r.netFallback || '—'}
                      title={r.net ? undefined : r.netFallback ? `Net adı etikete eşit: ${r.netFallback}` : undefined}
                      onChange={(e) => updateEdges([r.edgeId], { netName: e.target.value.trim() || undefined })}
                      style={cellInputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={r.gauge}
                      placeholder="—"
                      onChange={(e) => updateEdges([r.edgeId], { gauge: e.target.value.trim() || undefined })}
                      style={cellInputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span
                        style={{
                          ...swatch,
                          background: r.color2
                            ? `repeating-linear-gradient(45deg, ${r.color || '#94a3b8'} 0 4px, ${r.color2} 4px 8px)`
                            : r.color || 'transparent',
                        }}
                      />
                      <input
                        value={r.color}
                        placeholder="color"
                        onChange={(e) => updateEdges([r.edgeId], { color: e.target.value.trim() || undefined })}
                        style={{ ...cellInputStyle, width: 56 }}
                      />
                      <input
                        value={r.color2}
                        placeholder="color 2"
                        onChange={(e) => updateEdges([r.edgeId], { color2: e.target.value.trim() || undefined })}
                        style={{ ...cellInputStyle, width: 56 }}
                      />
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={r.harnessId}
                      onChange={(e) => assignHarness([r.edgeId], e.target.value || undefined)}
                      style={rowSelectStyle}
                    >
                      <option value="">—</option>
                      {bundles.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name || '(unnamed)'}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <button
                      style={goBtnStyle}
                      title="Go to this wire on the canvas"
                      onClick={() => {
                        goToEdge(r.edgeId);
                        onClose();
                      }}
                    >
                      ↗
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const panelStyle: CSSProperties = {
  width: 1120,
  maxWidth: 'calc(100vw - 28px)',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
};
const titleBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px 8px 14px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
};
const titleStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
};
const closeBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel-alt)',
};
const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};
const searchInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 8px 5px 26px',
  fontSize: 12,
  background: 'var(--panel)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};
const toolBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 28,
  padding: '0 9px',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};
const primaryBtnStyle: CSSProperties = {
  height: 28,
  padding: '0 12px',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 11,
  fontWeight: 800,
  cursor: 'pointer',
};
const selectStyle: CSSProperties = {
  height: 28,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 12,
  padding: '0 6px',
  maxWidth: 180,
};
const listStyle: CSSProperties = { overflow: 'auto', flex: 1 };
const tableStyle: CSSProperties = {
  width: '100%',
  minWidth: 964,
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: 12,
};
const thStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  textAlign: 'left',
  padding: '7px 5px',
  borderBottom: '2px solid var(--panel-border)',
  background: 'var(--panel-alt)',
  color: 'var(--text-muted)',
  fontWeight: 800,
  whiteSpace: 'nowrap',
  zIndex: 1,
};
const tdStyle: CSSProperties = {
  padding: '3px 5px',
  borderBottom: '1px solid var(--panel-border)',
  color: 'var(--text)',
  verticalAlign: 'middle',
  overflow: 'hidden',
};
const selectedRowStyle: CSSProperties = { background: 'var(--accent-soft)' };
const truncStyle: CSSProperties = {
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const cellInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid transparent',
  borderRadius: 3,
  background: 'var(--panel-alt)',
  color: 'var(--text)',
  padding: '3px 5px',
  fontSize: 11.5,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const rowSelectStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 3,
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 11.5,
  padding: '2px 4px',
  boxSizing: 'border-box',
};
const swatch: CSSProperties = {
  display: 'inline-block',
  width: 18,
  height: 11,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.25)',
};
const goBtnStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 3,
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: '2px 0',
  fontSize: 12,
};
